import type {
  AccountType,
  NewBookingNotification,
  NewFileNoteCommentMentionNotification,
  NewFileNoteMentionNotification,
  NewFileNoteReplyNotification,
  NewInteractionCommentMentionNotification,
  NewInteractionMentionNotification,
  NewLeadTaskNotification,
  NewUnisonProjectNoteCommentMentionNotification,
  NewUnisonProjectNoteMentionNotification,
  NewUnisonProjectNoteReplyNotification,
  NewUnisonProjectTaskNotification,
  Notification,
} from '@witmetrics/utils';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import {
  isNewBookingNotification,
  isNewFileNoteCommentMention,
  isNewFileNoteMention,
  isNewFileNoteReply,
  isNewInteractionCommentMention,
  isNewInteractionMention,
  isNewLeadTaskNotification,
  isNewUnisonProjectNoteCommentMention,
  isNewUnisonProjectNoteMention,
  isNewUnisonProjectNoteReply,
  isNewUnisonProjectTask,
} from '@witmetrics/utils/notifications';
import { API } from '@/api';
import { useSessionStorage } from '@/hooks/useStorage';

const { PRACTICE, LAB } = ACCOUNT_TYPES;

export function useStoredAccountId() {
  return useSessionStorage<number | null>('activeAccountId', null, true);
}

export async function updateActiveAccount(
  accountType: AccountType,
  accountId: number
) {
  return (await API.Users.setActiveAccount(accountType, accountId))
    .activeAccount;
}

export function updateReadNotification(notificationId: number) {
  return API.Notifications.updateNotification(notificationId, { isRead: true });
}

export function getNotificationAccount(notification: Notification) {
  if (notification.practiceId) {
    return { accountType: PRACTICE, accountId: notification.practiceId };
  }
  return { accountType: LAB, accountId: notification.labId! };
}

export function isProjectNotification(
  notification: Notification
): notification is
  | NewUnisonProjectTaskNotification
  | NewUnisonProjectNoteMentionNotification
  | NewUnisonProjectNoteCommentMentionNotification
  | NewUnisonProjectNoteReplyNotification {
  return (
    isNewUnisonProjectTask(notification) ||
    isNewUnisonProjectNoteMention(notification) ||
    isNewUnisonProjectNoteCommentMention(notification) ||
    isNewUnisonProjectNoteReply(notification)
  );
}

export function isFileNotification(
  notification: Notification
): notification is
  | NewFileNoteMentionNotification
  | NewFileNoteCommentMentionNotification
  | NewFileNoteReplyNotification {
  return (
    isNewFileNoteMention(notification) ||
    isNewFileNoteCommentMention(notification) ||
    isNewFileNoteReply(notification)
  );
}

export function isLeadNotification(
  notification: Notification
): notification is
  | NewBookingNotification
  | NewLeadTaskNotification
  | NewInteractionMentionNotification
  | NewInteractionCommentMentionNotification {
  return (
    isNewBookingNotification(notification) ||
    isNewLeadTaskNotification(notification) ||
    isNewInteractionMention(notification) ||
    isNewInteractionCommentMention(notification)
  );
}
